'use client';

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/shared/Button';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { addUTMs } from '@/lib/utils';
import type { GlobalContent } from '@/lib/content-schema';

interface Props {
  title: string;
  buttonText: string;
  buttonVisible: boolean;
  global: GlobalContent;
}

export function CommunityCTA({ title, buttonText, buttonVisible, global: g }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const posthog = usePostHog();
  const ctaHref = addUTMs(g.appUrl, 'club_cta');

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-zibarit-verde-oscuro to-zibarit-gris-oscuro">
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="initial"
        animate={isInView ? 'animate' : 'initial'}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.h2
          variants={fadeInUp}
          className="text-title text-3xl md:text-4xl lg:text-5xl text-zibarit-bone mb-10"
        >
          {title}
        </motion.h2>

        {buttonVisible && (
          <motion.div variants={fadeInUp}>
            <Button
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              magnetic
              size="lg"
              className="inline-block"
              data-ph-capture
              onClick={() => posthog?.capture('landing_cta_clicked', { button_text: buttonText, destination_url: ctaHref, page: 'club' })}
            >
              {buttonText}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
